import { authMetadata } from './metadata';
import {
    accountClient,
    authClient,
    appNotificationsClient,
    checkoutsClient,
    quotesClient,
    customersClient,
    dashboardClient,
    desktopReleasesClient,
    filamentsClient,
    salesChannelsClient,
    paymentFeesClient,
    jobsClient,
    ordersClient,
    printerMaintenancesClient,
    printersClient,
    productsClient,
    replacementPartsClient,
    operationalCostsClient,
    tutorialVideosClient,
    printerModelsClient,
    ticketsClient,
    transactionsClient,
    featureFlagsClient,
    subscriptionBillingClient,
    subscriptionPlansClient,
    commercePartnersClient,
    usersClient,
} from './clients';
import { axiosResponse, grpcCall } from './errors';
import { timestampToISO } from './timestamp';
import { dashboardDataFromProto } from './dashboardMapper';
import {
    accountResponseFromProto,
    appNotificationFromProto,
    checkoutFromProto,
    checkoutListItemFromProto,
    quoteFromProto,
    quoteListItemFromProto,
    createAppNotificationResponseFromProto,
    customerFromProto,
    customerListItemFromProto,
    deleteResponseFromProto,
    desktopReleaseFromProto,
    filamentFromProto,
    filamentListItemFromProto,
    filamentUsageFromProto,
    salesChannelFromProto,
    paymentFeeFromProto,
    jobFromProto,
    loginResponseFromProto,
    registerResponseFromProto,
    nestedListResponse,
    orderFromProto,
    orderListItemFromProto,
    orderSummaryFromProto,
    paginatedListResponse,
    printerFromProto,
    printerListItemFromProto,
    printerMaintenanceFromProto,
    printerStatsResponseFromProto,
    maintenanceTaskTypeFromProto,
    upcomingMaintenancesResponseFromProto,
    productFromProto,
    productListItemFromProto,
    productSummaryFromProto,
    reconciliationFromProto,
    replacementPartItemFromProto,
    replacementPartsListResponseFromProto,
    operationalCostFromProto,
    operationalCostsListResponseFromProto,
    tutorialVideoFromProto,
    printerModelFromProto,
    printerModelsListResponseFromProto,
    successResponseFromProto,
    ticketFromProto,
    ticketMetaFromProto,
    transactionFromProto,
    unreadCountResponseFromProto,
    updateAvatarResponseFromProto,
    updatePasswordResponseFromProto,
    updateProfileResponseFromProto,
    userFromProto,
    adminUserFromProto,
    adminUsersListResponseFromProto,
    integrationsResponseFromProto,
    featureFlagFromProto,
    billingExemptionFromProto,
    subscriptionPlanFromProto,
    mySubscriptionFromProto,
    planInfoFromProto,
    planQuotaUsagesFromProto,
    migratePlanResponseRestFromProto,
    listSubscriptionPlansResponseFromProto,
    subscriptionChargeFromProto,
    subscriptionChargeSectionsFromProto,
    subscriptionPaymentFromProto,
    subscriptionPaymentRecordRestFromProto,
    mpSubscriptionAdminFromProto,
    evaluatedFeaturesFromProto,
    commercePartnerFromProto,
    commercePartnerVisitFromProto,
    commercePartnersDashboardFromProto,
    commercePartnersMapFromProto,
} from './restMappers';
import {
    buildCreateAppNotificationRequest,
    buildCreateCheckoutRequest,
    buildCreateQuoteRequest,
    buildCreateCustomerRequest,
    buildCreateDesktopReleaseRequest,
    buildCreateFilamentRequest,
    buildListFilamentUsagesRequest,
    buildCreateSalesChannelRequest,
    buildCreatePaymentFeeRequest,
    buildCreateJobRequest,
    buildCreateMaintenanceRequest,
    buildCreatePrinterRequest,
    buildCreateProductRequest,
    buildCreateReplacementPartRequest,
    buildCreateOperationalCostRequest,
    buildListOperationalCostsRequest,
    buildUpdateOperationalCostRequest,
    buildCreateTutorialVideoRequest,
    buildListTutorialVideosRequest,
    buildUpdateTutorialVideoRequest,
    buildReorderTutorialVideosRequest,
    buildCreatePrinterModelRequest,
    buildCreateTicketRequest,
    buildCreateTransactionRequest,
    buildAddTicketMessageRequest,
    buildAdjustProductStockRequest,
    buildListAppNotificationsRequest,
    buildListSentAppNotificationsRequest,
    buildListTicketsRequest,
    buildListJobsRequest,
    buildListOrdersRequest,
    buildPayOrderRequest,
    buildUpdateOrderCommissionRequest,
    buildUpdateOrderStagesRequest,
    buildListTransactionsRequest,
    buildReconciliationRequest,
    buildOrdersSummaryRequest,
    buildListMaintenancesRequest,
    buildListReplacementPartsRequest,
    buildListPrinterModelsRequest,
    buildLoginRequest,
    buildRegisterRequest,
    buildPaginationRequest,
    buildProductsSummaryRequest,
    buildPrinterStatsRequest,
    buildUpdateAvatarRequest,
    buildUpdateCheckoutRequest,
    buildUpdateQuoteRequest,
    buildUpdateQuoteStatusRequest,
    buildConvertQuoteToCheckoutRequest,
    buildUpdateCustomerRequest,
    buildUpdateDesktopReleaseRequest,
    buildUpdateFilamentRequest,
    buildUpdateSalesChannelRequest,
    buildUpdatePaymentFeeRequest,
    buildUpdateJobRequest,
    buildUpdateMaintenanceRequest,
    buildUpdatePasswordRequest,
    buildUpdatePrinterRequest,
    buildUpdateProductRequest,
    buildUpdateProfileRequest,
    buildUpdateReplacementPartRequest,
    buildUpdatePrinterModelRequest,
    buildUpdateTicketStatusRequest,
    buildUpdateTransactionRequest,
    buildListCommercePartnersRequest,
    buildListUsersRequest,
    buildCreateUserRequest,
    buildUpdateUserRequest,
    buildCreateCommercePartnerRequest,
    buildUpdateCommercePartnerRequest,
    buildUpdateFeatureFlagRequest,
    buildCreateFeatureFlagRequest,
    buildCreateBillingExemptionRequest,
    buildCreateSubscriptionPlanRequest,
    buildUpdateSubscriptionPlanRequest,
    buildAttachSubscriptionChargeRequest,
    buildMigratePlanRequest,
    buildSubmitSubscriptionPaymentProofRequest,
    buildRejectSubscriptionPaymentRequest,
    buildPartnerLineInputs,
} from './requestBuilders';
import { parseRequestBody } from './requestBody';
import { dateStringToTimestamp } from './timestamp';

const SKIP_PATH_PREFIXES = [
    'users/activate-account',
    'users/reset-password',
    'users/redefine-password',
    'quick-checkout',
    'checkouts/batch',
];

function parseUrl(url, params = {}) {
    let raw = String(url || '').trim();
    // Axios pode enviar URL absoluta após merge com baseURL
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
        try {
            const parsed = new URL(raw);
            raw = parsed.pathname + parsed.search;
        } catch {
            // mantém raw
        }
    }
    raw = raw.replace(/^\/api\/?/i, '').replace(/^\//, '');
    const [pathPart, queryPart] = raw.split('?');
    const path = pathPart.replace(/\/$/, '') || '';
    const search = new URLSearchParams(queryPart || '');
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.set(key, String(value));
        }
    });
    const query = {};
    search.forEach((value, key) => {
        query[key] = value;
    });
    return {
        path,
        segments: path.split('/').filter(Boolean),
        query,
    };
}

function shouldSkipGrpc(config) {
    if (config?.data instanceof FormData) {
        return true;
    }
    const contentType = config?.headers?.['Content-Type'] || config?.headers?.['content-type'] || '';
    if (typeof contentType === 'string' && contentType.includes('multipart/form-data')) {
        return true;
    }
    const { path } = parseUrl(config?.url, config?.params);
    if (!path) {
        return true;
    }
    if (SKIP_PATH_PREFIXES.some((prefix) => {
        if (prefix.endsWith('/')) {
            return path.startsWith(prefix);
        }
        return path === prefix || path.startsWith(`${prefix}/`);
    })) {
        return true;
    }
    return false;
}

function meta() {
    return authMetadata();
}

function getDashboard(startDate, endDate) {
    const { GetDashboardRequest } = require('./proto/acompanha/dashboard/v1/dashboard_pb');
    const request = new GetDashboardRequest();
    const startTs = dateStringToTimestamp(startDate);
    const endTs = dateStringToTimestamp(endDate);
    if (startTs) {
        request.setStartDate(startTs);
    }
    if (endTs) {
        request.setEndDate(endTs);
    }
    return grpcCall(dashboardClient().getDashboard(request, meta())).then((res) => {
        const data = dashboardDataFromProto(res.getData());
        return axiosResponse({ data });
    });
}

async function routeGet(path, segments, query) {
    if (path === 'dashboard' || path === 'dashboard/') {
        return getDashboard(query.start_date, query.end_date);
    }

    if (path === 'auth/login' || path === 'auth/register') {
        return null;
    }

    if (path === 'auth/me') {
        const { MeRequest } = require('./proto/acompanha/auth/v1/auth_pb');
        const req = new MeRequest();
        return grpcCall(authClient().me(req, meta())).then((res) => (
            axiosResponse({ data: userFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'users' && segments.length === 1) {
        const req = buildListUsersRequest(query);
        return grpcCall(usersClient().listUsers(req, meta())).then((res) => (
            axiosResponse(adminUsersListResponseFromProto(res))
        ));
    }

    if (segments[0] === 'users' && segments.length === 2) {
        const { GetUserRequest } = require('./proto/acompanha/users/v1/users_pb');
        const req = new GetUserRequest();
        req.setUuid(segments[1]);
        return grpcCall(usersClient().getUser(req, meta())).then((res) => (
            axiosResponse({ data: adminUserFromProto(res.getData()) })
        ));
    }

    if (path === 'me/integrations' || path === 'me/integrations/') {
        const { GetIntegrationsRequest } = require('./proto/acompanha/account/v1/account_pb');
        return grpcCall(accountClient().getIntegrations(new GetIntegrationsRequest(), meta())).then((res) => (
            axiosResponse(integrationsResponseFromProto(res))
        ));
    }

    if (path === 'me/account') {
        const { GetAccountRequest } = require('./proto/acompanha/account/v1/account_pb');
        return grpcCall(accountClient().getAccount(new GetAccountRequest(), meta())).then((res) => (
            axiosResponse(accountResponseFromProto(res))
        ));
    }

    if (path === 'plans') {
        const { ListSubscriptionPlansRequest } = require('./proto/acompanha/account/v1/account_pb');
        return grpcCall(accountClient().listSubscriptionPlans(new ListSubscriptionPlansRequest(), {})).then((res) => (
            axiosResponse(listSubscriptionPlansResponseFromProto(res))
        ));
    }

    if (path === 'me/subscription') {
        const { GetMySubscriptionRequest } = require('./proto/acompanha/account/v1/account_pb');
        return grpcCall(accountClient().getMySubscription(new GetMySubscriptionRequest(), meta())).then((res) => (
            axiosResponse({ data: mySubscriptionFromProto(res.getData()) })
        ));
    }

    if (path === 'me/subscription/checkout/status') {
        const { GetSubscriptionCheckoutStatusRequest } = require('./proto/acompanha/account/v1/account_pb');
        const req = new GetSubscriptionCheckoutStatusRequest();
        req.setPreapprovalId(query.preapproval_id || query.preapprovalId || '');
        return grpcCall(accountClient().getSubscriptionCheckoutStatus(req, meta())).then((res) => (
            axiosResponse({
                data: {
                    mp_subscription_status: res.getMpSubscriptionStatus?.() || '',
                    checkout_pending: Boolean(res.getCheckoutPending?.()),
                    trial_active: Boolean(res.getTrialActive?.()),
                    grace_until: res.getGraceUntil?.() || '',
                    plan: {
                        plan: planInfoFromProto(res.getPlan?.()),
                    },
                },
            })
        ));
    }

    if (path === 'me/plan-usage') {
        const { GetPlanUsageRequest } = require('./proto/acompanha/account/v1/account_pb');
        return grpcCall(accountClient().getPlanUsage(new GetPlanUsageRequest(), meta())).then((res) => (
            axiosResponse({ data: { usage: planQuotaUsagesFromProto(res.getUsageList()) } })
        ));
    }

    if (segments[0] === 'subscription-charges' && segments.length === 1) {
        const { ListSubscriptionChargesRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        return grpcCall(subscriptionBillingClient().listSubscriptionCharges(new ListSubscriptionChargesRequest(), meta())).then((res) => (
            axiosResponse({
                charges: (res.getChargesList() || []).map(subscriptionChargeFromProto).filter(Boolean),
                sections: subscriptionChargeSectionsFromProto(res.getSections()),
            })
        ));
    }

    if (segments[0] === 'subscription-charges' && segments.length === 2) {
        const { GetSubscriptionChargeRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        const req = new GetSubscriptionChargeRequest();
        req.setUserUuid(segments[1]);
        return grpcCall(subscriptionBillingClient().getSubscriptionCharge(req, meta())).then((res) => (
            axiosResponse({ charge: subscriptionChargeFromProto(res.getCharge()) })
        ));
    }

    if (segments[0] === 'subscription-payments' && segments.length === 1) {
        const { ListSubscriptionPaymentsRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        return grpcCall(subscriptionBillingClient().listSubscriptionPayments(new ListSubscriptionPaymentsRequest(), meta())).then((res) => (
            axiosResponse({
                payments: (res.getPaymentsList() || []).map(subscriptionPaymentFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'subscription-payments' && segments.length === 2) {
        const { GetSubscriptionPaymentRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        const req = new GetSubscriptionPaymentRequest();
        req.setUuid(segments[1]);
        return grpcCall(subscriptionBillingClient().getSubscriptionPayment(req, meta())).then((res) => (
            axiosResponse({ payment: subscriptionPaymentFromProto(res.getPayment()) })
        ));
    }

    if (segments[0] === 'mp-subscriptions' && segments.length === 1) {
        const { ListMPSubscriptionsRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        const req = new ListMPSubscriptionsRequest();
        if (query.q) {
            req.setQ(String(query.q));
        }
        if (query.status) {
            req.setStatus(String(query.status));
        }
        return grpcCall(subscriptionBillingClient().listMPSubscriptions(req, meta())).then((res) => (
            axiosResponse({
                data: (res.getDataList() || []).map(mpSubscriptionAdminFromProto).filter(Boolean),
            })
        ));
    }

    if (path === 'jobs/printer-stats') {
        const req = buildPrinterStatsRequest(query);
        return grpcCall(jobsClient().printerStats(req, meta())).then((res) => (
            axiosResponse(printerStatsResponseFromProto(res))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 1) {
        const req = buildListJobsRequest(query);
        return grpcCall(jobsClient().listJobs(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'jobs',
                (res.getJobsList() || []).map(jobFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 2) {
        const { GetJobRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
        const req = new GetJobRequest();
        req.setUuid(segments[1]);
        return grpcCall(jobsClient().getJob(req, meta())).then((res) => (
            axiosResponse({ success: true, data: jobFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 1) {
        const { ListPrintersRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new ListPrintersRequest();
        req.setPagination(buildPaginationRequest(query));
        return grpcCall(printersClient().listPrinters(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'printers',
                (res.getPrintersList() || []).map(printerListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'printers' && segments.length === 2) {
        const { GetPrinterRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new GetPrinterRequest();
        req.setUuid(segments[1]);
        return grpcCall(printersClient().getPrinter(req, meta())).then((res) => (
            axiosResponse({ data: printerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments[1] === 'low-stock') {
        const { ListLowStockFilamentsRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new ListLowStockFilamentsRequest();
        return grpcCall(filamentsClient().listLowStockFilaments(req, meta())).then((res) => (
            axiosResponse({
                materials: (res.getMaterialsList() || []).map(filamentListItemFromProto).filter(Boolean),
                total: res.getTotal() || 0,
            })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 1) {
        const { ListFilamentsRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new ListFilamentsRequest();
        req.setPagination(buildPaginationRequest(query));
        if (query.archived) {
            req.setArchived(String(query.archived));
        }
        if (query.assignment_scope && typeof req.setAssignmentScope === 'function') {
            req.setAssignmentScope(String(query.assignment_scope));
        }
        req.setSortBy(String(query.sort_by || 'created_at'));
        req.setSortOrder(String(query.sort_order || 'desc'));
        return grpcCall(filamentsClient().listFilaments(req, meta())).then((res) => (
            axiosResponse({
                ...paginatedListResponse(
                    'materials',
                    (res.getMaterialsList() || []).map(filamentListItemFromProto).filter(Boolean),
                    res.getMeta(),
                ),
                archived_total: res.getArchivedTotal() || 0,
            })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 2) {
        const { GetFilamentRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new GetFilamentRequest();
        req.setUuid(segments[1]);
        return grpcCall(filamentsClient().getFilament(req, meta())).then((res) => (
            axiosResponse({ data: filamentFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 3 && segments[2] === 'usages') {
        const req = buildListFilamentUsagesRequest(segments[1], query);
        return grpcCall(filamentsClient().listFilamentUsages(req, meta())).then((res) => (
            axiosResponse({
                ...paginatedListResponse(
                    'usages',
                    (res.getUsagesList() || []).map(filamentUsageFromProto).filter(Boolean),
                    res.getMeta(),
                ),
                total_grams: res.getTotalGrams() || 0,
            })
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 1) {
        const { ListSalesChannelsRequest } = require('./proto/acompanha/sales_channels/v1/sales_channels_pb');
        const req = new ListSalesChannelsRequest();
        return grpcCall(salesChannelsClient().listSalesChannels(req, meta())).then((res) => (
            axiosResponse({
                channels: (res.getChannelsList() || []).map(salesChannelFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 1) {
        const { ListPaymentFeesRequest } = require('./proto/acompanha/payment_fees/v1/payment_fees_pb');
        const req = new ListPaymentFeesRequest();
        return grpcCall(paymentFeesClient().listPaymentFees(req, meta())).then((res) => (
            axiosResponse({
                fees: (res.getFeesList() || []).map(paymentFeeFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'products' && segments[1] === 'summary') {
        const req = buildProductsSummaryRequest(query);
        return grpcCall(productsClient().getProductsSummary(req, meta())).then((res) => (
            axiosResponse(productSummaryFromProto(res))
        ));
    }

    if (segments[0] === 'products' && segments.length === 1) {
        const { ListProductsRequest } = require('./proto/acompanha/products/v1/products_pb');
        const req = new ListProductsRequest();
        req.setPagination(buildPaginationRequest(query));
        req.setSortBy(String(query.sort_by || 'created_at'));
        req.setSortOrder(String(query.sort_order || 'desc'));
        return grpcCall(productsClient().listProducts(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'products',
                (res.getProductsList() || []).map(productListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'products' && segments.length === 2) {
        const { GetProductRequest } = require('./proto/acompanha/products/v1/products_pb');
        const req = new GetProductRequest();
        req.setUuid(segments[1]);
        return grpcCall(productsClient().getProduct(req, meta())).then((res) => (
            axiosResponse({ data: productFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'customers' && segments.length === 1) {
        const { ListCustomersRequest } = require('./proto/acompanha/customers/v1/customers_pb');
        const req = new ListCustomersRequest();
        req.setPagination(buildPaginationRequest(query));
        if (query.category) {
            req.setCategory(String(query.category));
        }
        if (query.origin_sales_channel_uuid) {
            req.setOriginSalesChannelUuid(String(query.origin_sales_channel_uuid));
        }
        if (query.sort_by) {
            req.setSortBy(String(query.sort_by));
        }
        if (query.sort_order) {
            req.setSortOrder(String(query.sort_order));
        }
        return grpcCall(customersClient().listCustomers(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'customers',
                (res.getCustomersList() || []).map(customerListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'customers' && segments.length === 2) {
        const { GetCustomerRequest } = require('./proto/acompanha/customers/v1/customers_pb');
        const req = new GetCustomerRequest();
        req.setUuid(segments[1]);
        return grpcCall(customersClient().getCustomer(req, meta())).then((res) => (
            axiosResponse({ data: customerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 1) {
        const { ListCheckoutsRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
        const req = new ListCheckoutsRequest();
        req.setPagination(buildPaginationRequest(query));
        return grpcCall(checkoutsClient().listCheckouts(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'checkouts',
                (res.getCheckoutsList() || []).map(checkoutListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 2) {
        const { GetCheckoutRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
        const req = new GetCheckoutRequest();
        req.setUuid(segments[1]);
        return grpcCall(checkoutsClient().getCheckout(req, meta())).then((res) => (
            axiosResponse({ data: checkoutFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 1) {
        const { ListQuotesRequest } = require('./proto/acompanha/quotes/v1/quotes_pb');
        const req = new ListQuotesRequest();
        req.setPagination(buildPaginationRequest(query));
        if (query.status) {
            req.setStatus(String(query.status));
        }
        const archivedRaw = String(query.archived || '').toLowerCase();
        req.setArchived(archivedRaw === '1' || archivedRaw === 'true' || archivedRaw === 'yes');
        return grpcCall(quotesClient().listQuotes(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'quotes',
                (res.getQuotesList() || []).map(quoteListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 2) {
        const { GetQuoteRequest } = require('./proto/acompanha/quotes/v1/quotes_pb');
        const req = new GetQuoteRequest();
        req.setUuid(segments[1]);
        return grpcCall(quotesClient().getQuote(req, meta())).then((res) => (
            axiosResponse({ data: quoteFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'orders' && segments[1] === 'summary') {
        const req = buildOrdersSummaryRequest(query);
        return grpcCall(ordersClient().getOrdersSummary(req, meta())).then((res) => (
            axiosResponse(orderSummaryFromProto(res))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 1) {
        const req = buildListOrdersRequest(query);
        return grpcCall(ordersClient().listOrders(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'orders',
                (res.getOrdersList() || []).map(orderListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 3 && segments[2] === 'transaction') {
        const { GetOrderTransactionRequest } = require('./proto/acompanha/orders/v1/orders_pb');
        const req = new GetOrderTransactionRequest();
        req.setOrderUuid(segments[1]);
        return grpcCall(ordersClient().getOrderTransaction(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    transaction: transactionFromProto(res.getTransaction()),
                },
            })
        ));
    }

    if (segments[0] === 'orders' && segments.length === 2) {
        const { GetOrderRequest } = require('./proto/acompanha/orders/v1/orders_pb');
        const req = new GetOrderRequest();
        req.setUuid(segments[1]);
        return grpcCall(ordersClient().getOrder(req, meta())).then((res) => (
            axiosResponse({ success: true, data: orderFromProto(res.getData()) })
        ));
    }

    if (path === 'transactions/reconciliation') {
        const req = buildReconciliationRequest(query);
        return grpcCall(transactionsClient().reconciliation(req, meta())).then((res) => (
            axiosResponse(reconciliationFromProto(res.getData()))
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 1) {
        const req = buildListTransactionsRequest(query);
        return grpcCall(transactionsClient().listTransactions(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'transactions',
                (res.getTransactionsList() || []).map(transactionFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 2) {
        const { GetTransactionRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
        const req = new GetTransactionRequest();
        req.setUuid(segments[1]);
        return grpcCall(transactionsClient().getTransaction(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    transaction: transactionFromProto(res.getTransaction()),
                },
            })
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 1) {
        const req = buildListMaintenancesRequest(query);
        return grpcCall(printerMaintenancesClient().listMaintenances(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'maintenances',
                (res.getMaintenancesList() || []).map(printerMaintenanceFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments[1] === 'upcoming') {
        const { ListUpcomingMaintenancesRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = new ListUpcomingMaintenancesRequest();
        return grpcCall(printerMaintenancesClient().listUpcomingMaintenances(req, meta())).then((res) => (
            axiosResponse(upcomingMaintenancesResponseFromProto(res))
        ));
    }

    // Contadores usam agregação REST (sem teto de paginação do gRPC).
    if (segments[0] === 'printer-maintenances' && segments[1] === 'summary') {
        return null;
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 2) {
        const { GetMaintenanceRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = new GetMaintenanceRequest();
        req.setUuid(segments[1]);
        return grpcCall(printerMaintenancesClient().getMaintenance(req, meta())).then((res) => (
            axiosResponse({ success: true, data: printerMaintenanceFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'replacement-parts' && segments.length === 1) {
        const req = buildListReplacementPartsRequest(query);
        return grpcCall(replacementPartsClient().listReplacementParts(req, meta())).then((res) => (
            axiosResponse(replacementPartsListResponseFromProto(res))
        ));
    }

    if (segments[0] === 'operational-costs' && segments.length === 1) {
        const req = buildListOperationalCostsRequest(query);
        return grpcCall(operationalCostsClient().listOperationalCosts(req, meta())).then((res) => (
            axiosResponse(operationalCostsListResponseFromProto(res))
        ));
    }

    if (segments[0] === 'replacement-parts' && segments.length === 2) {
        const { GetReplacementPartRequest } = require('./proto/acompanha/replacement_parts/v1/replacement_parts_pb');
        const req = new GetReplacementPartRequest();
        req.setUuid(segments[1]);
        return grpcCall(replacementPartsClient().getReplacementPart(req, meta())).then((res) => (
            axiosResponse({ data: replacementPartItemFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'operational-costs' && segments.length === 2) {
        const { GetOperationalCostRequest } = require('./proto/acompanha/operational_costs/v1/operational_costs_pb');
        const req = new GetOperationalCostRequest();
        req.setUuid(segments[1]);
        return grpcCall(operationalCostsClient().getOperationalCost(req, meta())).then((res) => (
            axiosResponse({ data: operationalCostFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printer-models' && segments.length === 1) {
        const req = buildListPrinterModelsRequest(query);
        return grpcCall(printerModelsClient().listPrinterModels(req, {})).then((res) => (
            axiosResponse(printerModelsListResponseFromProto(res))
        ));
    }

    if (path === 'printer-models/maintenance-task-types') {
        const { ListMaintenanceTaskTypesRequest } = require('./proto/acompanha/printer_models/v1/printer_models_pb');
        const req = new ListMaintenanceTaskTypesRequest();
        return grpcCall(printerModelsClient().listMaintenanceTaskTypes(req, meta())).then((res) => (
            axiosResponse({
                data: {
                    items: (res.getItemsList() || []).map(maintenanceTaskTypeFromProto).filter(Boolean),
                },
            })
        ));
    }

    if (segments[0] === 'printer-models' && segments.length === 2) {
        const { GetPrinterModelRequest } = require('./proto/acompanha/printer_models/v1/printer_models_pb');
        const req = new GetPrinterModelRequest();
        req.setUuid(segments[1]);
        return grpcCall(printerModelsClient().getPrinterModel(req, meta())).then((res) => (
            axiosResponse({ data: printerModelFromProto(res.getData()) })
        ));
    }

    if (path === 'notifications/sent') {
        const req = buildListSentAppNotificationsRequest(query);
        return grpcCall(appNotificationsClient().listSentAppNotifications(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'notifications',
                (res.getNotificationsList() || []).map(appNotificationFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'notifications' && segments.length === 1) {
        const req = buildListAppNotificationsRequest(query);
        return grpcCall(appNotificationsClient().listAppNotifications(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'notifications',
                (res.getNotificationsList() || []).map(appNotificationFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (path === 'notifications/unread-count') {
        const { GetUnreadCountRequest } = require('./proto/acompanha/app_notifications/v1/app_notifications_pb');
        const req = new GetUnreadCountRequest();
        return grpcCall(appNotificationsClient().getUnreadCount(req, meta())).then((res) => (
            axiosResponse(unreadCountResponseFromProto(res))
        ));
    }

    if (path === 'desktop-releases/latest') {
        const { GetLatestDesktopReleaseRequest } = require('./proto/acompanha/desktop_releases/v1/desktop_releases_pb');
        const req = new GetLatestDesktopReleaseRequest();
        return grpcCall(desktopReleasesClient().getLatestDesktopRelease(req, meta())).then((res) => (
            axiosResponse({ success: true, data: desktopReleaseFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'desktop-releases' && segments.length === 1) {
        const { ListDesktopReleasesRequest } = require('./proto/acompanha/desktop_releases/v1/desktop_releases_pb');
        const req = new ListDesktopReleasesRequest();
        return grpcCall(desktopReleasesClient().listDesktopReleases(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: (res.getDataList() || []).map(desktopReleaseFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'tutorial-videos' && segments.length === 1) {
        const req = buildListTutorialVideosRequest(query);
        return grpcCall(tutorialVideosClient().listTutorialVideos(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    items: (res.getItemsList() || []).map(tutorialVideoFromProto).filter(Boolean),
                },
            })
        ));
    }

    if (segments[0] === 'tutorial-videos' && segments.length === 2) {
        const { GetTutorialVideoRequest } = require('./proto/acompanha/tutorial_videos/v1/tutorial_videos_pb');
        const req = new GetTutorialVideoRequest();
        req.setUuid(segments[1]);
        return grpcCall(tutorialVideosClient().getTutorialVideo(req, meta())).then((res) => (
            axiosResponse({ success: true, data: tutorialVideoFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'desktop-releases' && segments.length === 2) {
        const { GetDesktopReleaseRequest } = require('./proto/acompanha/desktop_releases/v1/desktop_releases_pb');
        const req = new GetDesktopReleaseRequest();
        req.setUuid(segments[1]);
        return grpcCall(desktopReleasesClient().getDesktopRelease(req, meta())).then((res) => (
            axiosResponse({ success: true, data: desktopReleaseFromProto(res.getData()) })
        ));
    }

    if (path === 'tickets/meta') {
        const { GetTicketMetaRequest } = require('./proto/acompanha/tickets/v1/tickets_pb');
        const req = new GetTicketMetaRequest();
        return grpcCall(ticketsClient().getTicketMeta(req, meta())).then((res) => (
            axiosResponse(ticketMetaFromProto(res))
        ));
    }

    if (segments[0] === 'tickets' && segments.length === 1) {
        const req = buildListTicketsRequest(query);
        return grpcCall(ticketsClient().listTickets(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'tickets',
                (res.getDataList() || []).map(ticketFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'tickets' && segments.length === 2) {
        const { GetTicketRequest } = require('./proto/acompanha/tickets/v1/tickets_pb');
        const req = new GetTicketRequest();
        req.setUuid(segments[1]);
        return grpcCall(ticketsClient().getTicket(req, meta())).then((res) => (
            axiosResponse({ success: true, data: ticketFromProto(res.getData()) })
        ));
    }

    if (path === 'features') {
        const { GetEvaluatedFeaturesRequest } = require('./proto/acompanha/feature_flags/v1/feature_flags_pb');
        return grpcCall(featureFlagsClient().getEvaluatedFeatures(new GetEvaluatedFeaturesRequest(), meta())).then((res) => (
            axiosResponse(evaluatedFeaturesFromProto(res))
        ));
    }

    if (segments[0] === 'feature-flags' && segments.length === 1) {
        const { ListFeatureFlagsRequest } = require('./proto/acompanha/feature_flags/v1/feature_flags_pb');
        return grpcCall(featureFlagsClient().listFeatureFlags(new ListFeatureFlagsRequest(), meta())).then((res) => (
            axiosResponse({ data: (res.getDataList() || []).map(featureFlagFromProto).filter(Boolean) })
        ));
    }

    if (segments[0] === 'feature-flags' && segments.length === 2) {
        const { GetFeatureFlagRequest } = require('./proto/acompanha/feature_flags/v1/feature_flags_pb');
        const req = new GetFeatureFlagRequest();
        req.setKey(segments[1]);
        return grpcCall(featureFlagsClient().getFeatureFlag(req, meta())).then((res) => (
            axiosResponse({ data: featureFlagFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'subscription-plans' && segments.length === 1) {
        const { ListSubscriptionPlansRequest } = require('./proto/acompanha/subscription_plans/v1/subscription_plans_pb');
        return grpcCall(subscriptionPlansClient().listSubscriptionPlans(new ListSubscriptionPlansRequest(), meta())).then((res) => (
            axiosResponse({ data: (res.getDataList() || []).map(subscriptionPlanFromProto).filter(Boolean) })
        ));
    }

    if (segments[0] === 'subscription-plans' && segments.length === 2) {
        const { GetSubscriptionPlanRequest } = require('./proto/acompanha/subscription_plans/v1/subscription_plans_pb');
        const req = new GetSubscriptionPlanRequest();
        req.setUuid(segments[1]);
        return grpcCall(subscriptionPlansClient().getSubscriptionPlan(req, meta())).then((res) => (
            axiosResponse({ data: subscriptionPlanFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'subscription-billing' && segments[1] === 'exemptions' && segments.length === 2) {
        const { ListBillingExemptionsRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        return grpcCall(subscriptionBillingClient().listBillingExemptions(new ListBillingExemptionsRequest(), meta())).then((res) => (
            axiosResponse({ data: (res.getDataList() || []).map(billingExemptionFromProto).filter(Boolean) })
        ));
    }

    if (path === 'commerce-partners/dashboard') {
        const { GetCommercePartnersDashboardRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        return grpcCall(commercePartnersClient().getCommercePartnersDashboard(new GetCommercePartnersDashboardRequest(), meta())).then((res) => (
            axiosResponse(commercePartnersDashboardFromProto(res))
        ));
    }

    if (path === 'commerce-partners/calendar') {
        const { ListCommercePartnerVisitsRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new ListCommercePartnerVisitsRequest();
        if (query.from) req.setFrom(String(query.from));
        if (query.to) req.setTo(String(query.to));
        return grpcCall(commercePartnersClient().listCommercePartnerVisits(req, meta())).then((res) => (
            axiosResponse({
                data: (res.getDataList() || []).map(commercePartnerVisitFromProto).filter(Boolean),
                from: res.getFrom(),
                to: res.getTo(),
            })
        ));
    }

    if (path === 'commerce-partners/map') {
        const { GetCommercePartnersMapRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new GetCommercePartnersMapRequest();
        if (query.date) req.setDate(String(query.date));
        return grpcCall(commercePartnersClient().getCommercePartnersMap(req, meta())).then((res) => (
            axiosResponse(commercePartnersMapFromProto(res))
        ));
    }

    if (segments[0] === 'commerce-partners' && segments[1] === 'visits' && segments.length === 3) {
        const { GetCommercePartnerVisitRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new GetCommercePartnerVisitRequest();
        req.setVisitUuid(segments[2]);
        return grpcCall(commercePartnersClient().getCommercePartnerVisit(req, meta())).then((res) => (
            axiosResponse({ data: commercePartnerVisitFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 1) {
        const req = buildListCommercePartnersRequest(query);
        return grpcCall(commercePartnersClient().listCommercePartners(req, meta())).then((res) => (
            axiosResponse({
                data: (res.getDataList() || []).map(commercePartnerFromProto).filter(Boolean),
                total: res.getTotal(),
                page: res.getPage(),
                limit: res.getLimit(),
            })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 2) {
        const { GetCommercePartnerRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new GetCommercePartnerRequest();
        req.setUuid(segments[1]);
        return grpcCall(commercePartnersClient().getCommercePartner(req, meta())).then((res) => (
            axiosResponse({ data: commercePartnerFromProto(res.getData()) })
        ));
    }

    return null;
}

async function routePost(path, segments, data) {
    if (path === 'auth/login') {
        const req = buildLoginRequest(data || {});
        return grpcCall(authClient().login(req, meta())).then((res) => (
            axiosResponse(loginResponseFromProto(res))
        ));
    }

    if (path === 'auth/register') {
        const req = buildRegisterRequest(data || {});
        return grpcCall(authClient().register(req, meta())).then((res) => (
            axiosResponse(registerResponseFromProto(res))
        ));
    }

    if (path === 'me/plan/migrate') {
        const req = buildMigratePlanRequest(data || {});
        return grpcCall(accountClient().migratePlan(req, meta())).then((res) => (
            axiosResponse({ data: migratePlanResponseRestFromProto(res) })
        ));
    }

    if (path === 'me/subscription/checkout') {
        const { StartSubscriptionCheckoutRequest } = require('./proto/acompanha/account/v1/account_pb');
        return grpcCall(accountClient().startSubscriptionCheckout(new StartSubscriptionCheckoutRequest(), meta())).then((res) => (
            axiosResponse({
                data: {
                    checkout_url: res.getCheckoutUrl?.() || '',
                    mp_preapproval_id: res.getMpPreapprovalId?.() || '',
                    exempt: Boolean(res.getExempt?.()),
                },
            })
        ));
    }

    if (path === 'me/subscription/payment-proof') {
        const req = buildSubmitSubscriptionPaymentProofRequest(data || {});
        return grpcCall(accountClient().submitSubscriptionPaymentProof(req, meta())).then((res) => (
            axiosResponse({ data: subscriptionPaymentRecordRestFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'users' && segments.length === 1) {
        const req = buildCreateUserRequest(data || {});
        return grpcCall(usersClient().createUser(req, meta())).then((res) => (
            axiosResponse({ data: adminUserFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'subscription-payments' && segments.length === 3 && segments[2] === 'approve') {
        const { ApproveSubscriptionPaymentRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        const req = new ApproveSubscriptionPaymentRequest();
        req.setUuid(segments[1]);
        return grpcCall(subscriptionBillingClient().approveSubscriptionPayment(req, meta())).then((res) => (
            axiosResponse({ success: true, payment: subscriptionPaymentFromProto(res.getPayment()) })
        ));
    }

    if (segments[0] === 'subscription-payments' && segments.length === 3 && segments[2] === 'reject') {
        const req = buildRejectSubscriptionPaymentRequest(segments[1], data || {});
        return grpcCall(subscriptionBillingClient().rejectSubscriptionPayment(req, meta())).then((res) => (
            axiosResponse({ success: true, payment: subscriptionPaymentFromProto(res.getPayment()) })
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 1) {
        const req = buildCreateJobRequest(data || {});
        return grpcCall(jobsClient().createJob(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: jobFromProto(res.getData()),
                warnings: res.getWarningsList() || [],
            })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 1) {
        const req = buildCreatePrinterRequest(data || {});
        return grpcCall(printersClient().createPrinter(req, meta())).then((res) => (
            axiosResponse({ data: printerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 1) {
        const req = buildCreateFilamentRequest(data || {});
        return grpcCall(filamentsClient().createFilament(req, meta())).then((res) => (
            axiosResponse({
                data: filamentFromProto(res.getData()),
                created: (res.getCreatedList() || []).map(filamentFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 3 && segments[2] === 'archive') {
        const { SetFilamentArchivedRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new SetFilamentArchivedRequest();
        req.setUuid(segments[1]);
        req.setArchived(data?.archived !== false);
        return grpcCall(filamentsClient().setFilamentArchived(req, meta())).then((res) => (
            axiosResponse({ data: filamentFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 3 && segments[2] === 'assign-printers') {
        const {
            AssignFilamentToPrintersRequest,
            FilamentPrinterAssignInput,
        } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new AssignFilamentToPrintersRequest();
        req.setFilamentUuid(segments[1]);
        (data?.assignments || []).forEach((item) => {
            const row = new FilamentPrinterAssignInput();
            row.setPrinterUuid(item.printer_uuid || '');
            row.setLocation(item.location || '');
            req.addAssignments(row);
        });
        return grpcCall(filamentsClient().assignFilamentToPrinters(req, meta())).then((res) => (
            axiosResponse({ success: res.getSuccess() })
        ));
    }

    if (segments[0] === 'operational-costs' && segments.length === 3 && segments[2] === 'archive') {
        const { SetOperationalCostArchivedRequest } = require('./proto/acompanha/operational_costs/v1/operational_costs_pb');
        const req = new SetOperationalCostArchivedRequest();
        req.setUuid(segments[1]);
        req.setArchived(data?.archived !== false);
        return grpcCall(operationalCostsClient().setOperationalCostArchived(req, meta())).then((res) => (
            axiosResponse({ data: operationalCostFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 1) {
        const req = buildCreateSalesChannelRequest(data || {});
        return grpcCall(salesChannelsClient().createSalesChannel(req, meta())).then((res) => (
            axiosResponse({ data: salesChannelFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 1) {
        const req = buildCreatePaymentFeeRequest(data || {});
        return grpcCall(paymentFeesClient().createPaymentFee(req, meta())).then((res) => (
            axiosResponse({ data: paymentFeeFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'products' && segments.length === 3 && segments[2] === 'stock') {
        const req = buildAdjustProductStockRequest(segments[1], data || {});
        return grpcCall(productsClient().adjustProductStock(req, meta())).then((res) => (
            axiosResponse({ data: productListItemFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'products' && segments.length === 1) {
        const req = buildCreateProductRequest(data || {});
        return grpcCall(productsClient().createProduct(req, meta())).then((res) => (
            axiosResponse({ data: productFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'customers' && segments.length === 1) {
        const req = buildCreateCustomerRequest(data || {});
        return grpcCall(customersClient().createCustomer(req, meta())).then((res) => (
            axiosResponse({ data: customerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 1) {
        const req = buildCreateCheckoutRequest(data || {});
        return grpcCall(checkoutsClient().createCheckout(req, meta())).then((res) => (
            axiosResponse({ success: true, data: checkoutFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 1) {
        const req = buildCreateQuoteRequest(data || {});
        return grpcCall(quotesClient().createQuote(req, meta())).then((res) => (
            axiosResponse({ success: true, data: quoteFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 3 && segments[2] === 'status') {
        const req = buildUpdateQuoteStatusRequest(segments[1], data || {});
        return grpcCall(quotesClient().updateQuoteStatus(req, meta())).then((res) => (
            axiosResponse({ success: true, data: quoteFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 3 && segments[2] === 'archive') {
        const { ArchiveQuoteRequest } = require('./proto/acompanha/quotes/v1/quotes_pb');
        const req = new ArchiveQuoteRequest();
        req.setUuid(segments[1]);
        return grpcCall(quotesClient().archiveQuote(req, meta())).then((res) => (
            axiosResponse({ success: true, data: quoteFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 3 && segments[2] === 'unarchive') {
        const { UnarchiveQuoteRequest } = require('./proto/acompanha/quotes/v1/quotes_pb');
        const req = new UnarchiveQuoteRequest();
        req.setUuid(segments[1]);
        if (data?.status) {
            req.setStatus(String(data.status));
        }
        return grpcCall(quotesClient().unarchiveQuote(req, meta())).then((res) => (
            axiosResponse({ success: true, data: quoteFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 3 && segments[2] === 'convert-to-checkout') {
        const req = buildConvertQuoteToCheckoutRequest(segments[1], data || {});
        return grpcCall(quotesClient().convertQuoteToCheckout(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: quoteFromProto(res.getData()),
                checkout_uuid: res.getCheckoutUuid() || '',
                order_uuid: res.getCheckoutUuid() || '',
            })
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 1) {
        const req = buildCreateTransactionRequest(data || {});
        return grpcCall(transactionsClient().createTransaction(req, meta())).then((res) => (
            axiosResponse({ success: Boolean(res.getSuccess()) })
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments[1] === 'snooze-all') {
        const { SnoozeAllMaintenanceTasksRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = new SnoozeAllMaintenanceTasksRequest();
        req.setDays(Number(data?.days) || 0);
        return grpcCall(printerMaintenancesClient().snoozeAllMaintenanceTasks(req, meta())).then((res) => (
            axiosResponse({
                success: Boolean(res.getSuccess()),
                data: {
                    snoozed_until: timestampToISO(res.getSnoozedUntil()),
                    count: res.getCount() || 0,
                },
            })
        ));
    }

    if (segments[0] === 'printer-maintenances' && (segments[1] === 'snooze' || segments[1] === 'resume')) {
        const snoozing = segments[1] === 'snooze';
        const {
            SnoozeMaintenanceTaskRequest,
            ResumeMaintenanceTaskRequest,
        } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = snoozing ? new SnoozeMaintenanceTaskRequest() : new ResumeMaintenanceTaskRequest();
        req.setPrinterUuid(String(data?.printer_uuid || ''));
        req.setTaskKey(String(data?.task_key || ''));
        if (snoozing) {
            req.setDays(Number(data?.days) || 0);
        }
        const client = printerMaintenancesClient();
        const call = snoozing
            ? client.snoozeMaintenanceTask(req, meta())
            : client.resumeMaintenanceTask(req, meta());
        return grpcCall(call).then((res) => axiosResponse({ success: Boolean(res.getSuccess()) }));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 1) {
        const req = buildCreateMaintenanceRequest(data || {});
        return grpcCall(printerMaintenancesClient().createMaintenance(req, meta())).then((res) => (
            axiosResponse({ success: true, data: printerMaintenanceFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'replacement-parts' && segments.length === 1) {
        const req = buildCreateReplacementPartRequest(data || {});
        return grpcCall(replacementPartsClient().createReplacementPart(req, meta())).then((res) => (
            axiosResponse({ data: replacementPartItemFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'operational-costs' && segments.length === 1) {
        const req = buildCreateOperationalCostRequest(data || {});
        return grpcCall(operationalCostsClient().createOperationalCost(req, meta())).then((res) => (
            axiosResponse({ data: operationalCostFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printer-models' && segments.length === 1) {
        const req = buildCreatePrinterModelRequest(data || {});
        return grpcCall(printerModelsClient().createPrinterModel(req, meta())).then((res) => (
            axiosResponse({ data: printerModelFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'notifications' && segments.length === 1) {
        const req = buildCreateAppNotificationRequest(data || {});
        return grpcCall(appNotificationsClient().createAppNotification(req, meta())).then((res) => (
            axiosResponse(createAppNotificationResponseFromProto(res), 201)
        ));
    }

    if (path === 'notifications/mark-all-read') {
        const { MarkAllAppNotificationsReadRequest } = require('./proto/acompanha/app_notifications/v1/app_notifications_pb');
        const req = new MarkAllAppNotificationsReadRequest();
        return grpcCall(appNotificationsClient().markAllAppNotificationsRead(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (path === 'notifications/delete-all') {
        const { DeleteAllAppNotificationsRequest } = require('./proto/acompanha/app_notifications/v1/app_notifications_pb');
        const req = new DeleteAllAppNotificationsRequest();
        return grpcCall(appNotificationsClient().deleteAllAppNotifications(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'notifications' && segments.length === 3 && segments[2] === 'read') {
        const { MarkAppNotificationReadRequest } = require('./proto/acompanha/app_notifications/v1/app_notifications_pb');
        const req = new MarkAppNotificationReadRequest();
        req.setUuid(segments[1]);
        return grpcCall(appNotificationsClient().markAppNotificationRead(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'desktop-releases' && segments.length === 1) {
        const req = buildCreateDesktopReleaseRequest(data || {});
        return grpcCall(desktopReleasesClient().createDesktopRelease(req, meta())).then((res) => (
            axiosResponse({ success: true, data: desktopReleaseFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'tutorial-videos' && segments.length === 1) {
        const req = buildCreateTutorialVideoRequest(data || {});
        return grpcCall(tutorialVideosClient().createTutorialVideo(req, meta())).then((res) => (
            axiosResponse({ success: true, data: tutorialVideoFromProto(res.getData()) }, 201)
        ));
    }

    if (path === 'tutorial-videos/reorder') {
        const req = buildReorderTutorialVideosRequest(data || {});
        return grpcCall(tutorialVideosClient().reorderTutorialVideos(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    items: (res.getItemsList() || []).map(tutorialVideoFromProto).filter(Boolean),
                },
            })
        ));
    }

    if (segments[0] === 'tickets' && segments.length === 1) {
        const req = buildCreateTicketRequest(data || {});
        return grpcCall(ticketsClient().createTicket(req, meta())).then((res) => (
            axiosResponse({ success: true, data: ticketFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'tickets' && segments.length === 3 && segments[2] === 'messages') {
        const req = buildAddTicketMessageRequest(segments[1], data || {});
        return grpcCall(ticketsClient().addTicketMessage(req, meta())).then((res) => (
            axiosResponse({ success: true, data: ticketFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'feature-flags' && segments.length === 1) {
        const req = buildCreateFeatureFlagRequest(data || {});
        return grpcCall(featureFlagsClient().createFeatureFlag(req, meta())).then((res) => (
            axiosResponse({ success: true, data: featureFlagFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'subscription-plans' && segments.length === 1) {
        const req = buildCreateSubscriptionPlanRequest(data || {});
        return grpcCall(subscriptionPlansClient().createSubscriptionPlan(req, meta())).then((res) => (
            axiosResponse({ success: true, data: subscriptionPlanFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'feature-flags' && segments.length === 3 && segments[2] === 'users') {
        const { AddFeatureFlagUserRequest } = require('./proto/acompanha/feature_flags/v1/feature_flags_pb');
        const req = new AddFeatureFlagUserRequest();
        req.setKey(segments[1]);
        req.setUserUuid(String(data?.user_uuid || ''));
        return grpcCall(featureFlagsClient().addFeatureFlagUser(req, meta())).then((res) => (
            axiosResponse({ success: true, data: featureFlagFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'subscription-billing' && segments[1] === 'exemptions' && segments.length === 2) {
        const req = buildCreateBillingExemptionRequest(data || {});
        return grpcCall(subscriptionBillingClient().createBillingExemption(req, meta())).then((res) => (
            axiosResponse({ success: true, data: billingExemptionFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 1) {
        const req = buildCreateCommercePartnerRequest(data || {});
        return grpcCall(commercePartnersClient().createCommercePartner(req, meta())).then((res) => (
            axiosResponse({ success: true, data: commercePartnerFromProto(res.getData()) }, 201)
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 3 && segments[2] === 'orders') {
        const {
            CreateCommercePartnerOrderRequest,
            CommercePartnerVisitLineInput,
        } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new CreateCommercePartnerOrderRequest();
        req.setUuid(segments[1]);
        req.setNotes(String(data?.notes || ''));
        req.setPaid(Boolean(data?.paid));
        req.setLinesList(buildPartnerLineInputs(data?.lines, CommercePartnerVisitLineInput));
        return grpcCall(commercePartnersClient().createCommercePartnerOrder(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    order: { uuid: res.getOrderUuid() },
                    visit: { uuid: res.getVisitUuid() },
                },
            }, 201)
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 4 && segments[2] === 'stock' && segments[3] === 'deliver') {
        const {
            DeliverCommercePartnerStockRequest,
            CommercePartnerVisitLineInput,
        } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new DeliverCommercePartnerStockRequest();
        req.setUuid(segments[1]);
        req.setLinesList(buildPartnerLineInputs(data?.lines, CommercePartnerVisitLineInput));
        return grpcCall(commercePartnersClient().deliverCommercePartnerStock(req, meta())).then((res) => (
            axiosResponse({ success: true, data: commercePartnerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 4 && segments[2] === 'schedule' && segments[3] === 'regenerate') {
        const { RegenerateCommercePartnerScheduleRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new RegenerateCommercePartnerScheduleRequest();
        req.setUuid(segments[1]);
        return grpcCall(commercePartnersClient().regenerateCommercePartnerSchedule(req, meta())).then((res) => (
            axiosResponse({ success: true, data: commercePartnerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments[1] === 'visits' && segments.length === 4 && segments[3] === 'complete') {
        const {
            CompleteCommercePartnerVisitRequest,
            CommercePartnerVisitLineInput,
        } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new CompleteCommercePartnerVisitRequest();
        req.setVisitUuid(segments[2]);
        req.setNotes(String(data?.notes || ''));
        req.setPaid(Boolean(data?.paid));
        req.setVisitedOn(String(data?.visited_on || ''));
        req.setPaymentMethod(String(data?.payment_method || ''));
        req.setPaymentDate(String(data?.payment_date || ''));
        req.setLinesList(buildPartnerLineInputs(data?.lines, CommercePartnerVisitLineInput));
        return grpcCall(commercePartnersClient().completeCommercePartnerVisit(req, meta())).then((res) => (
            axiosResponse({ success: true, data: commercePartnerVisitFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments[1] === 'visits' && segments.length === 4 && segments[3] === 'pay') {
        const { MarkCommercePartnerVisitPaidRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new MarkCommercePartnerVisitPaidRequest();
        req.setVisitUuid(segments[2]);
        req.setPaymentMethod(String(data?.payment_method || ''));
        req.setPaymentDate(String(data?.payment_date || ''));
        return grpcCall(commercePartnersClient().markCommercePartnerVisitPaid(req, meta())).then((res) => (
            axiosResponse({ success: true, data: commercePartnerVisitFromProto(res.getData()) })
        ));
    }

    return null;
}

async function routePut(path, segments, data) {
    if (segments[0] === 'orders' && segments.length === 3 && segments[1] === 'pay-order') {
        const req = buildPayOrderRequest(segments[2], data || {});
        return grpcCall(ordersClient().payOrder(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res, 'Venda paga com sucesso.'))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 3 && segments[2] === 'commission') {
        const req = buildUpdateOrderCommissionRequest(segments[1], data || {});
        return grpcCall(ordersClient().updateOrderCommission(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: orderFromProto(res.getData()),
            })
        ));
    }

    if (segments[0] === 'users' && segments.length === 2) {
        const req = buildUpdateUserRequest(segments[1], data || {});
        return grpcCall(usersClient().updateUser(req, meta())).then((res) => (
            axiosResponse({ data: adminUserFromProto(res.getData()) })
        ));
    }

    if (path === 'me/avatar') {
        const req = buildUpdateAvatarRequest(data || {});
        return grpcCall(accountClient().updateAvatar(req, meta())).then((res) => (
            axiosResponse(updateAvatarResponseFromProto(res))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 2) {
        const req = buildUpdateJobRequest(segments[1], data || {});
        return grpcCall(jobsClient().updateJob(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: jobFromProto(res.getData()),
                warnings: res.getWarningsList() || [],
            })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 2) {
        const req = buildUpdatePrinterRequest(segments[1], data || {});
        return grpcCall(printersClient().updatePrinter(req, meta())).then((res) => (
            axiosResponse({ data: printerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 3 && segments[2] === 'filaments') {
        const {
            SetPrinterFilamentsRequest,
            PrinterFilamentInput,
        } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new SetPrinterFilamentsRequest();
        req.setPrinterUuid(segments[1]);
        (data?.filaments || []).forEach((item) => {
            const row = new PrinterFilamentInput();
            row.setFilamentUuid(item.filament_uuid || '');
            row.setLocation(item.location || '');
            req.addFilaments(row);
        });
        return grpcCall(printersClient().setPrinterFilaments(req, meta())).then((res) => (
            axiosResponse({
                filaments: (res.getFilamentsList() || [])
                    .map((item) => ({
                        uuid: item.getUuid?.() || '',
                        filament_uuid: item.getFilamentUuid?.() || '',
                        location: item.getLocation?.() || '',
                        sort_order: item.getSortOrder?.() || 0,
                        type: item.getType?.() || '',
                        color: item.getColor?.() || '',
                        color_label: item.getColorLabel?.() || '',
                        brand: item.getBrand?.() || '',
                        printer_uuid: item.getPrinterUuid?.() || '',
                        printer_name: item.getPrinterName?.() || '',
                    })),
            })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 2) {
        const req = buildUpdateFilamentRequest(segments[1], data || {});
        return grpcCall(filamentsClient().updateFilament(req, meta())).then((res) => (
            axiosResponse({ data: filamentFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 2) {
        const req = buildUpdateSalesChannelRequest(segments[1], data || {});
        return grpcCall(salesChannelsClient().updateSalesChannel(req, meta())).then((res) => (
            axiosResponse({ data: salesChannelFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 2) {
        const req = buildUpdatePaymentFeeRequest(segments[1], data || {});
        return grpcCall(paymentFeesClient().updatePaymentFee(req, meta())).then((res) => (
            axiosResponse({ data: paymentFeeFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'products' && segments.length === 2) {
        const req = buildUpdateProductRequest(segments[1], data || {});
        return grpcCall(productsClient().updateProduct(req, meta())).then((res) => (
            axiosResponse({ data: productFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'customers' && segments.length === 2) {
        const req = buildUpdateCustomerRequest(segments[1], data || {});
        return grpcCall(customersClient().updateCustomer(req, meta())).then((res) => (
            axiosResponse({ data: customerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 2) {
        const req = buildUpdateCheckoutRequest(segments[1], data || {});
        return grpcCall(checkoutsClient().updateCheckout(req, meta())).then((res) => (
            axiosResponse({ success: true, data: checkoutFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 2) {
        const req = buildUpdateQuoteRequest(segments[1], data || {});
        return grpcCall(quotesClient().updateQuote(req, meta())).then((res) => (
            axiosResponse({ success: true, data: quoteFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 2) {
        const req = buildUpdateMaintenanceRequest(segments[1], data || {});
        return grpcCall(printerMaintenancesClient().updateMaintenance(req, meta())).then((res) => (
            axiosResponse({ success: true, data: printerMaintenanceFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'replacement-parts' && segments.length === 2) {
        const req = buildUpdateReplacementPartRequest(segments[1], data || {});
        return grpcCall(replacementPartsClient().updateReplacementPart(req, meta())).then((res) => (
            axiosResponse({ data: replacementPartItemFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'operational-costs' && segments.length === 2) {
        const req = buildUpdateOperationalCostRequest(segments[1], data || {});
        return grpcCall(operationalCostsClient().updateOperationalCost(req, meta())).then((res) => (
            axiosResponse({ data: operationalCostFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printer-models' && segments.length === 2) {
        const req = buildUpdatePrinterModelRequest(segments[1], data || {});
        return grpcCall(printerModelsClient().updatePrinterModel(req, meta())).then((res) => (
            axiosResponse({ data: printerModelFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 2) {
        const req = buildUpdateTransactionRequest(segments[1], data || {});
        return grpcCall(transactionsClient().updateTransaction(req, meta())).then((res) => (
            axiosResponse({ success: Boolean(res.getSuccess()) })
        ));
    }

    if (segments[0] === 'desktop-releases' && segments.length === 2) {
        const req = buildUpdateDesktopReleaseRequest(segments[1], data || {});
        return grpcCall(desktopReleasesClient().updateDesktopRelease(req, meta())).then((res) => (
            axiosResponse({ success: true, data: desktopReleaseFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'tutorial-videos' && segments.length === 2) {
        const req = buildUpdateTutorialVideoRequest(segments[1], data || {});
        return grpcCall(tutorialVideosClient().updateTutorialVideo(req, meta())).then((res) => (
            axiosResponse({ success: true, data: tutorialVideoFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'tickets' && segments.length === 3 && segments[2] === 'status') {
        const req = buildUpdateTicketStatusRequest(segments[1], data || {});
        return grpcCall(ticketsClient().updateTicketStatus(req, meta())).then((res) => (
            axiosResponse({ success: true, data: ticketFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'feature-flags' && segments.length === 2) {
        const req = buildUpdateFeatureFlagRequest(segments[1], data || {});
        return grpcCall(featureFlagsClient().updateFeatureFlag(req, meta())).then((res) => (
            axiosResponse({ success: true, data: featureFlagFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'subscription-plans' && segments.length === 2) {
        const req = buildUpdateSubscriptionPlanRequest(segments[1], data || {});
        return grpcCall(subscriptionPlansClient().updateSubscriptionPlan(req, meta())).then((res) => (
            axiosResponse({ success: true, data: subscriptionPlanFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 2) {
        const req = buildUpdateCommercePartnerRequest(segments[1], data || {});
        return grpcCall(commercePartnersClient().updateCommercePartner(req, meta())).then((res) => (
            axiosResponse({ success: true, data: commercePartnerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'subscription-charges' && segments.length === 2) {
        const req = buildAttachSubscriptionChargeRequest(segments[1], data || {});
        return grpcCall(subscriptionBillingClient().attachSubscriptionCharge(req, meta())).then((res) => (
            axiosResponse({ success: true, charge: subscriptionChargeFromProto(res.getCharge()) })
        ));
    }

    return null;
}

async function routePatch(path, segments, data) {
    if (path === 'me') {
        const req = buildUpdateProfileRequest(data || {});
        return grpcCall(accountClient().updateProfile(req, meta())).then((res) => (
            axiosResponse(updateProfileResponseFromProto(res))
        ));
    }

    if (path === 'me/password') {
        const req = buildUpdatePasswordRequest(data || {});
        return grpcCall(accountClient().updatePassword(req, meta())).then((res) => (
            axiosResponse(updatePasswordResponseFromProto(res))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 3 && segments[2] === 'stages') {
        const req = buildUpdateOrderStagesRequest(segments[1], data || {});
        return grpcCall(ordersClient().updateOrderStages(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: orderFromProto(res.getData()),
            })
        ));
    }

    return null;
}

async function routeDelete(path, segments, query = {}) {
    if (segments[0] === 'users' && segments.length === 2) {
        const { DeleteUserRequest } = require('./proto/acompanha/users/v1/users_pb');
        const req = new DeleteUserRequest();
        req.setUuid(segments[1]);
        return grpcCall(usersClient().deleteUser(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 2) {
        const { DeleteJobRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
        const req = new DeleteJobRequest();
        req.setUuid(segments[1]);
        return grpcCall(jobsClient().deleteJob(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res, 'Job deleted successfully.'))
        ));
    }

    if (segments[0] === 'printers' && segments.length === 2) {
        const { DeletePrinterRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new DeletePrinterRequest();
        req.setUuid(segments[1]);
        return grpcCall(printersClient().deletePrinter(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'printers' && segments.length === 3 && segments[2] === 'filaments') {
        const { UnassignPrinterFilamentRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new UnassignPrinterFilamentRequest();
        req.setPrinterUuid(segments[1]);
        if (query.assignment_uuid) {
            req.setAssignmentUuid(String(query.assignment_uuid));
        }
        if (query.filament_uuid) {
            req.setFilamentUuid(String(query.filament_uuid));
        }
        return grpcCall(printersClient().unassignPrinterFilament(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 2) {
        const { DeleteFilamentRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new DeleteFilamentRequest();
        req.setUuid(segments[1]);
        return grpcCall(filamentsClient().deleteFilament(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 2) {
        const { DeleteSalesChannelRequest } = require('./proto/acompanha/sales_channels/v1/sales_channels_pb');
        const req = new DeleteSalesChannelRequest();
        req.setUuid(segments[1]);
        return grpcCall(salesChannelsClient().deleteSalesChannel(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 2) {
        const { DeletePaymentFeeRequest } = require('./proto/acompanha/payment_fees/v1/payment_fees_pb');
        const req = new DeletePaymentFeeRequest();
        req.setUuid(segments[1]);
        return grpcCall(paymentFeesClient().deletePaymentFee(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'products' && segments.length === 2) {
        const { DeleteProductRequest } = require('./proto/acompanha/products/v1/products_pb');
        const req = new DeleteProductRequest();
        req.setUuid(segments[1]);
        return grpcCall(productsClient().deleteProduct(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'customers' && segments.length === 2) {
        const { DeleteCustomerRequest } = require('./proto/acompanha/customers/v1/customers_pb');
        const req = new DeleteCustomerRequest();
        req.setUuid(segments[1]);
        return grpcCall(customersClient().deleteCustomer(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 2) {
        const { DeleteCheckoutRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
        const req = new DeleteCheckoutRequest();
        req.setUuid(segments[1]);
        return grpcCall(checkoutsClient().deleteCheckout(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'quotes' && segments.length === 2) {
        const { DeleteQuoteRequest } = require('./proto/acompanha/quotes/v1/quotes_pb');
        const req = new DeleteQuoteRequest();
        req.setUuid(segments[1]);
        return grpcCall(quotesClient().deleteQuote(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 2) {
        const { DeleteOrderRequest } = require('./proto/acompanha/orders/v1/orders_pb');
        const req = new DeleteOrderRequest();
        req.setUuid(segments[1]);
        return grpcCall(ordersClient().deleteOrder(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 2) {
        const { DeleteTransactionRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
        const req = new DeleteTransactionRequest();
        req.setUuid(segments[1]);
        return grpcCall(transactionsClient().deleteTransaction(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 2) {
        const { DeleteMaintenanceRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = new DeleteMaintenanceRequest();
        req.setUuid(segments[1]);
        return grpcCall(printerMaintenancesClient().deleteMaintenance(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'replacement-parts' && segments.length === 2) {
        const { DeleteReplacementPartRequest } = require('./proto/acompanha/replacement_parts/v1/replacement_parts_pb');
        const req = new DeleteReplacementPartRequest();
        req.setUuid(segments[1]);
        return grpcCall(replacementPartsClient().deleteReplacementPart(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'operational-costs' && segments.length === 2) {
        const { DeleteOperationalCostRequest } = require('./proto/acompanha/operational_costs/v1/operational_costs_pb');
        const req = new DeleteOperationalCostRequest();
        req.setUuid(segments[1]);
        return grpcCall(operationalCostsClient().deleteOperationalCost(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'printer-models' && segments.length === 2) {
        const { DeletePrinterModelRequest } = require('./proto/acompanha/printer_models/v1/printer_models_pb');
        const req = new DeletePrinterModelRequest();
        req.setUuid(segments[1]);
        return grpcCall(printerModelsClient().deletePrinterModel(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'subscription-plans' && segments.length === 2) {
        const { DeleteSubscriptionPlanRequest } = require('./proto/acompanha/subscription_plans/v1/subscription_plans_pb');
        const req = new DeleteSubscriptionPlanRequest();
        req.setUuid(segments[1]);
        return grpcCall(subscriptionPlansClient().deleteSubscriptionPlan(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'notifications' && segments.length === 2) {
        const { DeleteAppNotificationRequest } = require('./proto/acompanha/app_notifications/v1/app_notifications_pb');
        const req = new DeleteAppNotificationRequest();
        req.setUuid(segments[1]);
        return grpcCall(appNotificationsClient().deleteAppNotification(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'desktop-releases' && segments.length === 2) {
        const { DeleteDesktopReleaseRequest } = require('./proto/acompanha/desktop_releases/v1/desktop_releases_pb');
        const req = new DeleteDesktopReleaseRequest();
        req.setUuid(segments[1]);
        return grpcCall(desktopReleasesClient().deleteDesktopRelease(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'tutorial-videos' && segments.length === 2) {
        const { DeleteTutorialVideoRequest } = require('./proto/acompanha/tutorial_videos/v1/tutorial_videos_pb');
        const req = new DeleteTutorialVideoRequest();
        req.setUuid(segments[1]);
        return grpcCall(tutorialVideosClient().deleteTutorialVideo(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'feature-flags' && segments.length === 4 && segments[2] === 'users') {
        const { RemoveFeatureFlagUserRequest } = require('./proto/acompanha/feature_flags/v1/feature_flags_pb');
        const req = new RemoveFeatureFlagUserRequest();
        req.setKey(segments[1]);
        req.setUserUuid(segments[3]);
        return grpcCall(featureFlagsClient().removeFeatureFlagUser(req, meta())).then((res) => (
            axiosResponse({ success: true, data: featureFlagFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'subscription-billing' && segments[1] === 'exemptions' && segments.length === 3) {
        const { RevokeBillingExemptionRequest } = require('./proto/acompanha/subscription_billing/v1/subscription_billing_pb');
        const req = new RevokeBillingExemptionRequest();
        req.setUuid(segments[2]);
        return grpcCall(subscriptionBillingClient().revokeBillingExemption(req, meta())).then((res) => (
            axiosResponse({ success: true, data: billingExemptionFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'commerce-partners' && segments.length === 2) {
        const { DeleteCommercePartnerRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new DeleteCommercePartnerRequest();
        req.setUuid(segments[1]);
        return grpcCall(commercePartnersClient().deleteCommercePartner(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'commerce-partners' && segments[1] === 'visits' && segments.length === 3) {
        const { DeleteCommercePartnerVisitRequest } = require('./proto/acompanha/commerce_partners/v1/commerce_partners_pb');
        const req = new DeleteCommercePartnerVisitRequest();
        req.setVisitUuid(segments[2]);
        return grpcCall(commercePartnersClient().deleteCommercePartnerVisit(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    return null;
}

/**
 * Axios-like transport router.
 * @param {{ method?: string, url?: string, params?: object, data?: any }} config
 * @returns {Promise<{ data: any, status: number, statusText: string, headers: object, config: object }>|null>}
 */
export async function grpcTransport(config) {
    if (shouldSkipGrpc(config)) {
        return null;
    }

    const method = String(config?.method || 'get').toLowerCase();
    const { path, segments, query } = parseUrl(config?.url, config?.params);

    let response = null;
    if (method === 'get') {
        response = await routeGet(path, segments, query);
    } else if (method === 'post') {
        response = await routePost(path, segments, parseRequestBody(config?.data));
    } else if (method === 'put') {
        response = await routePut(path, segments, parseRequestBody(config?.data));
    } else if (method === 'patch') {
        response = await routePatch(path, segments, parseRequestBody(config?.data));
    } else if (method === 'delete') {
        response = await routeDelete(path, segments, query);
    }

    return response ?? null;
}
